import React, { useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatCurrency, currencySymbol } from '../../../../utils/formatters';

// GraphQL identical to ValuationSnapshot.jsx
const GET_VALUATION_SNAPSHOT = gql`
  query GetValuationSnapshot($input: Input) {
    successionValuationData(input: $input) {
      valueOfPractice
      sumDiscountedCashFlows
      discountedTerminalValue
    }
  }
`;

const GET_CURRENT_USER_VAL = gql`
  query GetCurrentUserValForInsight {
    getCurrentUser {
      id
      valuation_last_accessed_date
    }
  }
`;

const currency = (n) => {
  const v = Number(n || 0);
  try {
    return formatCurrency(v);
  } catch (_) {
    return `${currencySymbol()}${Math.round(v).toLocaleString('en-US')}`;
  }
};

const EmptyState = () => (
  <div className="pl-2 pr-3 pt-2 pb-3 sm:pl-2 sm:pr-4 sm:py-3 h-full flex flex-col items-center justify-center text-center">
    <h3 className="text-succession-text-primary m-0 p-0"><FormattedMessage id="succession.valuation_insight.empty_title" /></h3>
    <div className="mt-3 flex items-center justify-center">
      <div className="relative" style={{ width: 153, height: 153 }}>
        <svg viewBox="0 0 42 42" className="transform -rotate-90">
          <circle cx="21" cy="21" r="18" fill="none" stroke="var(--succession-border)" strokeWidth="6" pathLength="100" strokeDasharray="100 0" opacity="0.3" />
          <circle cx="21" cy="21" r="18" fill="none" stroke="var(--succession-border)" strokeWidth="6" pathLength="100" strokeDasharray="50 50" opacity="0.2" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center px-2">
            <h4 className="text-succession-text-secondary font-semibold text-base">$0</h4>
            <div className="text-[10px] text-succession-text-secondary">—</div>
          </div>
        </div>
      </div>
    </div>
    <p className="text-succession-text-secondary text-sm max-w-md mt-3"><FormattedMessage id="succession.valuation_insight.empty_body" /></p>
    <div className="mt-2">
      <a href="/succession/valuation" className="inline-flex items-center px-4 py-2 rounded-md bg-succession-primary text-white hover:bg-succession-primary-hover text-sm"><FormattedMessage id="succession.valuation_insight.run_valuation" /></a>
    </div>
  </div>
);

const InsightBody = ({ data }) => {
  const intl = useIntl();
  const [hovered, setHovered] = useState(null); // 'cash' | 'term' | null
  const sum = Number(data.sumDiscountedCashFlows || 0);
  const term = Number(data.discountedTerminalValue || 0);
  const total = sum + term;
  const sumPctRaw = total > 0 ? (sum / total) * 100 : 50;
  const sumPct = Math.max(0, Math.min(100, sumPctRaw));
  const termPct = Math.max(0, Math.min(100, 100 - sumPct));
  const practiceValue = Number(data.valueOfPractice || total);
  // Theming per theming-implementation-guide.mdc
  // Cash Flows -> secondary color; Terminal Value -> primary color
  const COLOR_SUM = 'var(--succession-secondary, #002949)';
  const COLOR_TERM = 'var(--succession-primary, #4f46e5)';

  const cfPctDisplay = Math.round(sumPct);
  const termPctDisplay = Math.max(0, 100 - cfPctDisplay);

  // Hover states to emphasize segments/cards
  const isCashHovered = hovered === 'cash';
  const isTermHovered = hovered === 'term';

  // Tooltip content for donut hover
  const donutTooltipText = hovered === 'cash'
    ? intl.formatMessage({ id: 'succession.valuation_insight.cash_flows_tooltip_full' })
    : hovered === 'term'
      ? intl.formatMessage({ id: 'succession.valuation_insight.terminal_value_tooltip_full' })
      : null;

  // Theme-aware helpers for legibility
  const isDark = (() => { try { return document.documentElement.classList.contains('succession-dark'); } catch (_) { return false; } })();
  const CENTER_FILL = isDark ? 'transparent' : '#ffffff';
  const CENTER_STROKE = isDark ? 'transparent' : 'rgba(15,23,42,0.06)';
  const numberTextShadow = isDark ? '0 1px 2px rgba(0,0,0,0.6)' : 'none';
  const TERM_OPACITY_BASE = isDark ? 1 : 0.85; // lighten terminal ring in light mode
  const SUM_OPACITY_BASE = isDark ? 1 : 0.9;   // slightly softer cash arc in light mode

  return (
    <div className="pl-2 pr-3 pt-2 pb-3 sm:pl-2 sm:pr-4 sm:py-3 h-full flex flex-col">
      <h3 className="text-succession-text-primary m-0 p-0 mb-1"><FormattedMessage id="succession.valuation_insight.title" /></h3>
      <div className="text-succession-text-secondary text-xs mb-2"><FormattedMessage id="succession.valuation_insight.subtitle" /></div>

      <div className="text-center">
        <div className="text-base font-semibold text-succession-text-primary">
          <FormattedMessage id="succession.valuation_insight.business_valued_at" values={{ value: currency(practiceValue) }} />
        </div>
        <div className="text-xs text-succession-text-secondary">
          <FormattedMessage id="succession.valuation_insight.earnings_breakdown" values={{ cashPct: cfPctDisplay, termPct: termPctDisplay }} />
        </div>
      </div>

      <div className="mt-2 flex flex-col items-center justify-center flex-1">
        <div className="flex items-center justify-center">
          <div className="relative" style={{ width: 204, height: 204 }}>
            <svg viewBox="0 0 42 42" className="transform -rotate-90">
              {/* Outer separation ring to ensure clarity on light background */}
              {!isDark && (
                <circle
                  cx="21"
                  cy="21"
                  r="16.6"
                  fill="none"
                  stroke="var(--succession-border)"
                  strokeWidth={0.6}
                  opacity={0.6}
                  pathLength="100"
                  strokeDasharray="100 0"
                />
              )}
              {/* Terminal Value ring (base) */}
              <circle
                cx="21"
                cy="21"
                r="16"
                fill="none"
                stroke={COLOR_TERM}
                strokeWidth={8}
                opacity={isTermHovered ? 0.3 : (isCashHovered ? 0.6 : TERM_OPACITY_BASE)}
                pathLength="100"
                strokeDasharray="100 0"
                strokeDashoffset="0"
                onMouseEnter={() => setHovered('term')}
                onMouseLeave={() => setHovered(null)}
                style={{ cursor: 'pointer' }}
              />
              {/* Cash Flows arc */}
              <circle
                cx="21"
                cy="21"
                r="16"
                fill="none"
                stroke={COLOR_SUM}
                strokeWidth={isCashHovered ? 10 : 8}
                strokeLinecap="butt"
                opacity={isTermHovered ? 0.6 : SUM_OPACITY_BASE}
                pathLength="100"
                strokeDasharray={`${Math.min(100, Math.max(0, sumPct))} ${Math.max(0, 100 - Math.min(100, Math.max(0, sumPct)))}`}
                strokeDashoffset="0"
                onMouseEnter={() => setHovered('cash')}
                onMouseLeave={() => setHovered(null)}
                style={{ cursor: 'pointer' }}
              />
              {/* Subtle white outline for readability on dark backgrounds */}
              <circle
                cx="21"
                cy="21"
                r="16"
                fill="none"
                stroke={isDark ? 'rgba(255,255,255,0.15)' : 'transparent'}
                strokeWidth={1}
                pathLength="100"
                strokeDasharray="100 0"
                strokeDashoffset="0"
                style={{ pointerEvents: 'none' }}
              />
              {/* Terminal highlight arc overlay (only the terminal segment) */}
              {isTermHovered && (
                <circle
                  cx="21"
                  cy="21"
                  r="16"
                  fill="none"
                  stroke={COLOR_TERM}
                  strokeWidth={10}
                  pathLength="100"
                  strokeDasharray={`${termPct} ${100 - termPct}`}
                  strokeDashoffset={`${(100 - sumPct) % 100}`}
                  style={{ pointerEvents: 'none' }}
                />
              )}
              {/* Inner disk to guarantee number legibility in light mode */}
              <circle
                cx="21"
                cy="21"
                r="9.5"
                fill={CENTER_FILL}
                stroke={CENTER_STROKE}
                strokeWidth={isDark ? 0 : 0.5}
                style={{ pointerEvents: 'none' }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center px-2">
                <div className="text-sm font-semibold text-succession-text-primary"><FormattedMessage id="succession.valuation_insight.total" /></div>
                <div className="text-3xl font-extrabold text-succession-text-primary" style={{ textShadow: numberTextShadow }}>{currency(practiceValue)}</div>
              </div>
            </div>
            {donutTooltipText && (
              <div
                className="absolute border border-succession-border rounded shadow-succession-md text-xs px-3 py-2 max-w-xs"
                style={{ top: '50%', left: '100%', transform: 'translate(16px, -50%)', background: 'var(--succession-bg-primary)', color: 'var(--succession-text-secondary)' }}
              >
                {hovered === 'cash' ? 'Cash Flows: ' : 'Terminal Value: '}
                {donutTooltipText}
              </div>
            )}
          </div>
        </div>
        {/* Breakdown badges (very compact) */}
        <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
          <span
            className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full border ${isCashHovered ? 'border-succession-primary' : 'border-succession-border'} bg-succession-bg-primary text-xs`}
            onMouseEnter={() => setHovered('cash')}
            onMouseLeave={() => setHovered(null)}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: COLOR_SUM }}></span>
            <span className="text-succession-text-primary"><FormattedMessage id="succession.valuation_insight.cash_flows_label" /></span>
            <span className="text-succession-text-primary font-semibold">{currency(sum)}</span>
            <span className="text-succession-text-secondary">({cfPctDisplay}%)</span>
          </span>
          <span
            className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full border ${isTermHovered ? 'border-succession-primary' : 'border-succession-border'} bg-succession-bg-primary text-xs`}
            onMouseEnter={() => setHovered('term')}
            onMouseLeave={() => setHovered(null)}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: COLOR_TERM }}></span>
            <span className="text-succession-text-primary"><FormattedMessage id="succession.valuation_insight.terminal_value_label" /></span>
            <span className="text-succession-text-primary font-semibold">{currency(term)}</span>
            <span className="text-succession-text-secondary">({termPctDisplay}%)</span>
          </span>
        </div>
      </div>

      <div className="mt-auto pt-2 flex justify-center">
        <a href="/succession/valuation" className="text-sm text-succession-primary hover:text-succession-primary-hover"><FormattedMessage id="succession.valuation_insight.view_details" /></a>
      </div>
    </div>
  );
};

const ValuationInsight = () => {
  const { data: cu, loading: cuLoading, error: cuError } = useQuery(GET_CURRENT_USER_VAL, { fetchPolicy: 'cache-first', nextFetchPolicy: 'cache-first' });
  const lastAccessed = cu?.getCurrentUser?.valuation_last_accessed_date;
  const shouldSkipSnapshot = cuLoading || !lastAccessed;

  const { data, loading, error } = useQuery(GET_VALUATION_SNAPSHOT, { variables: { input: null }, skip: shouldSkipSnapshot, fetchPolicy: 'network-only', nextFetchPolicy: 'cache-first' });

  const overallLoading = cuLoading || (!shouldSkipSnapshot && loading);
  const overallError = cuError || error;

  if (overallLoading) {
    return <div className="p-4 text-succession-text-secondary text-sm"><FormattedMessage id="succession.valuation_insight.loading" /></div>;
  }
  if (overallError) {
    return <div className="p-4 text-succession-error text-sm"><FormattedMessage id="succession.valuation_insight.error" /></div>;
  }

  const snapshot = (!shouldSkipSnapshot && data) ? (data?.successionValuationData || null) : null;
  const hasData = Boolean(snapshot && (snapshot.valueOfPractice || snapshot.sumDiscountedCashFlows || snapshot.discountedTerminalValue));

  return (
    <div className="bg-transparent overflow-hidden shadow-succession-md rounded-lg h-full transition-all duration-300 border-0">
      {hasData ? <InsightBody data={snapshot} /> : <EmptyState />}
    </div>
  );
};

export default ValuationInsight;




